import { DigitalAddressScreeningResult } from '@/types/digital-address-screening';
import { RequestData } from '@ant-design/pro-table';
import { request } from '@/utils/request';
import { trim } from 'lodash';

export interface DigitalWithdrawListItem {
  id: string;
  date: string;
  status: DigitalWithdrawStatus;
  processingStatus?: ProcessingStatus | null;
  amount: string;
  fee: number;
  refId: string;
  withdrawAddressType: string;
  network: string;
  destinationAddress: string;
  publicTransactionHash: string;
  user: {
    id: string;
    name: string;
    email: string;
    accountId: string;
  };
  wallet: {
    id: string;
    label: string;
    name: string;
    accountNumber: string;
  };
  assetSymbol: string;
  description: string;
  compliance?: {
    digitalAddressScreening?: DigitalAddressScreeningResult;
  };
}

/**
 * Aggregated KYT risk summary for an account/wallet
 */
export interface AccountRiskSummary {
  maxRiskScore: number | null;
  avgRiskScore: number | null;
  flaggedCount: number;
  totalScreenings: number;
  lastScreeningAt: string | null;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
  /** Top risk categories seen for this wallet */
  topRiskCategories?: Array<{ category: string; count: number }>;
}

/**
 * Fireblocks transaction status information for display
 */
export interface FireblocksInfo {
  status: string | null;
  subStatus: string | null;
  transactionId: string | null;
  errorDescription: string | null;
}

export interface DigitalWithdrawDetail extends DigitalWithdrawListItem {
  purpose?: string;
  memo?: string;
  treasuryBalance?: {};
  returnForDepositId?: string;
  returnForDepositRefId?: string;
  travelRule: {
    walletOwnership?: string;
    walletType?: string;
    recipientFullName?: string;
    exchangePlatform?: string;
  };
  accountRisk?: AccountRiskSummary;
  /** Fireblocks transaction status and error information */
  fireblocksInfo?: FireblocksInfo;
  /** Processing status tracks Fireblocks operational state */
  processingStatus?: ProcessingStatus | null;
  /** Processing error from failed Fireblocks API call (when processingStatus is FAILED) */
  processingError?: string | null;
  /** Last-known pinned source vault — used to prefill the Retry modal. */
  lastWalletName?: string | null;
  lastWalletAccountId?: string | null;
  /** Timestamp the withdrawal was cancelled at (null when not cancelled) */
  cancelledAt?: string | null;
  /** Operator-supplied cancellation reason (null for system-originated cancels) */
  cancelReason?: string | null;
  /** Operator who cancelled the withdrawal (null for system-originated cancels) */
  cancelledBy?: { id: string; name: string; email: string } | null;
}

interface currencyResponse {
  type: string;
  name: string;
  sequence?: number;
  memoSupported: boolean;
}

export enum DigitalWithdrawStatus {
  REQUESTED = 'REQUESTED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  CANCEL = 'CANCEL'
}

/**
 * Processing status tracks the operational state of Fireblocks processing.
 * This is separate from the business status (DigitalWithdrawStatus).
 */
export enum ProcessingStatus {
  QUEUED = 'QUEUED',
  SUBMITTED = 'SUBMITTED',
  PENDING_AUTHORIZATION = 'PENDING_AUTHORIZATION',
  PENDING_SIGNATURE = 'PENDING_SIGNATURE',
  BROADCASTING = 'BROADCASTING',
  CONFIRMING = 'CONFIRMING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
  REJECTED = 'REJECTED',
  AML_BYPASS_REQUIRED = 'AML_BYPASS_REQUIRED'
}

export enum DigitalWithdrawEventTypes {
  DigitalWithdrawRequested = 'digital_withdraw.event.requested',
  DigitalWithdrawCancelled = 'digital_withdraw.event.cancelled',
  DigitalWithdrawProcessed = 'digital_withdraw.event.processed',
  DigitalWithdrawPendingConfirmation = 'digital_withdraw.event.pending_confirmation',
  DigitalWithdrawPendingManualIntervention = 'digital_withdraw.event.pending_with_error',
  DigitalWithdrawApproved = 'digital_withdraw.event.approved',
  DigitalWithdrawResetToRequested = 'digital_withdraw.event.reset_to_requested'
}

export async function queryDigitalWithdaw(params: any): Promise<RequestData<DigitalWithdrawListItem>> {
  const data = await request<RequestData<DigitalWithdrawListItem>>('/digital-withdraws', {
    params: {
      ...params,
      destinationAddress: params.destinationAddress ? trim(params.destinationAddress) : undefined,
      publicTransactionHash: params.publicTransactionHash ? trim(params.publicTransactionHash) : undefined
    }
  });
  return data;
}

export async function queryDigitalWithdrawDetail(params: { id: string }) {
  let data = await request<DigitalWithdrawDetail>(`/digital-withdraws/${params.id}`);

  const currencies = await request<currencyResponse[]>(`/currencies`);

  const currency = currencies.find((c) => c.name === data.assetSymbol);
  if (!currency || !currency.memoSupported) {
    data.memo = '-';
  }
  return {
    data
  };
}

export async function processDigitalWithdraw(params: {
  method: string;
  rawTransaction: string;
}): Promise<RequestData<DigitalWithdrawDetail>> {
  const data = await request<RequestData<DigitalWithdrawListItem>>('/digital-withdraws', {
    params: params,
    method: 'post'
  });
  return data;
}

export async function cancelDigitalWithdraw(params: { id: string }): Promise<RequestData<DigitalWithdrawDetail>> {
  const data = await request<RequestData<DigitalWithdrawListItem>>(`/digital-withdraws/${params.id}/cancel`, {
    data: params,
    method: 'post'
  });
  return data;
}

export async function editDigitalWithdrawDetail(params: {
  id: string;
  description: string;
  userId: string;
  _yotpToken: string;
  operatorRemark?: string;
  remarkChannel?: string;
}) {
  const postData = await request(`/digital-withdraws/${params.id}/edit`, {
    method: 'POST',
    data: {
      ...params,
      method: 'post'
    }
  });
  return {
    postData
  };
}

/**
 * Today's digital withdraw counts by status
 */
export interface DigitalWithdrawStats {
  pending: number;
  requested: number;
  success: number;
  cancelled: number;
  faulted: number;
}

/**
 * Get today's digital withdrawal counts by status
 */
export async function getDigitalWithdrawStats(): Promise<DigitalWithdrawStats> {
  return request<DigitalWithdrawStats>('/digital-withdraws/stats');
}

/**
 * Vault balance information for a specific vault account
 */
export interface VaultBalance {
  available: number;
  total: number;
}

/**
 * Fee asset information
 */
export interface FeeAssetInfo {
  symbol: string;
  fireblocksId: string | null;
  isSameAsWithdrawAsset: boolean;
  /** True when the chain asset is EIP gasless; UI skips gas-balance warnings. */
  isGasless?: boolean;
}

/**
 * Response from the vault-balances endpoint
 */
export interface VaultBalancesResponse {
  assetBalance: VaultBalance | null;
  gasBalance: VaultBalance | null;
  feeAsset: FeeAssetInfo;
  withdrawAsset: string;
  withdrawAssetFireblocksId?: string;
  chain: string;
  vaultAccountId: string;
  /** Fireblocks numeric accountId for the resolved vault; undefined on error paths before resolution. */
  vaultAccountNumber?: string;
  error?: string;
}

/**
 * Get vault balances for a digital withdrawal including gas/fee asset balance.
 * Queries the specific vault that will be used for the withdrawal.
 * @param id - The withdrawal ID
 * @param walletName - Optional custom wallet name. If not provided, uses default hot wallet.
 * @param walletAccountId - Optional Fireblocks vault numeric id to disambiguate same-name vaults.
 * @returns Asset balance and gas/fee asset balance for the selected vault
 */
export async function getVaultBalances(
  id: string,
  walletName?: string,
  walletAccountId?: string
): Promise<VaultBalancesResponse> {
  const params: Record<string, string> = {};
  if (walletName) params.walletName = walletName;
  if (walletAccountId) params.walletAccountId = walletAccountId;
  return request<VaultBalancesResponse>(`/digital-withdraws/${id}/vault-balances`, { params });
}

export type AvailableVault = {
  name: string;
  accountId: string;
  /** Total balance of the withdrawal's asset in the vault (native units, string). */
  total?: string;
  /** Available (non-pending) balance of the withdrawal's asset in the vault. */
  available?: string;
  /** Source deposit address for the withdrawal asset, if a registered deposit address exists for this (vault, asset) pair. */
  address?: string;
};

/**
 * Minimum query length for the server-side custom-wallet search. Mirrors
 * `SEARCH_MIN_LENGTH` in the backend — kept in sync manually since a single
 * constant can't be shared across the web and API bundles.
 */
export const VAULT_SEARCH_MIN_LENGTH = 4;

/**
 * Response from `GET /digital-withdraws/:id/destination-insights`. Powers
 * the anomaly banners on the detail page (self-transfer, first-time
 * destination, prior activity at this address).
 */
export interface DestinationInsights {
  selfTransfer:
    | {
        isOwned: true;
        ownerUserId: string;
        ownerName: string | null;
        ownerAccountId: string | null;
        /** true when the destination is the withdrawing user's own deposit address (circular transfer). */
        sameAsWithdrawer: boolean;
      }
    | { isOwned: false };
  history: {
    /** Prior successful withdrawals from the *same* user to this (address, chain). */
    sameUser: { count: number; mostRecentAt: string | null };
    /** Prior successful withdrawals from *other users of the same vendor* to this (address, chain). */
    otherUsers: { count: number; mostRecentAt: string | null };
  };
}

export async function getDestinationInsights(id: string): Promise<DestinationInsights> {
  return request<DestinationInsights>(`/digital-withdraws/${id}/destination-insights`);
}

/**
 * Deterministic cross-flow pattern findings for the withdrawal's user.
 * Populates the Issues panel on the detail page alongside
 * balance/screening/destination alerts. HIGH severity findings also
 * trigger the backend case workflow; MEDIUM/LOW are informational only.
 */
export interface PatternFindingView {
  patternType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  title: string;
  detail: string;
  evidence: {
    instructionIds: string[];
    timeWindow?: { start: string; end: string };
    metric?: number;
    baseline?: number;
  };
}

export interface PatternAnalysisResponse {
  findings: PatternFindingView[];
  worstSeverity: 'LOW' | 'MEDIUM' | 'HIGH' | null;
}

export async function getPatternAnalysis(id: string): Promise<PatternAnalysisResponse> {
  return request<PatternAnalysisResponse>(`/digital-withdraws/${id}/pattern-analysis`);
}

/**
 * List the end user's own Fireblocks vaults for a withdrawal — the set under
 * `{vendor.prefix}://u/{walletAccountNumber}` (new) or `/u/{userId}` (legacy).
 * Small, bounded result set suitable for pre-loading the picker.
 */
export async function getUserVaults(id: string): Promise<AvailableVault[]> {
  const res = await request<{ vaults: AvailableVault[] }>(
    `/digital-withdraws/${id}/user-vaults`
  );
  return res?.vaults ?? [];
}

/**
 * Server-side search over Fireblocks vaults for a withdrawal. Returns an
 * empty array for queries shorter than `VAULT_SEARCH_MIN_LENGTH` — the
 * backend short-circuits too, but we avoid the roundtrip here.
 */
export async function searchVaults(id: string, query: string): Promise<AvailableVault[]> {
  const q = query.trim();
  if (q.length < VAULT_SEARCH_MIN_LENGTH) return [];
  const res = await request<{ vaults: AvailableVault[] }>(
    `/digital-withdraws/${id}/search-vaults`,
    { params: { q } }
  );
  return res?.vaults ?? [];
}

/**
 * Retry a faulted digital withdrawal via Fireblocks
 * Only works for withdrawals with processingStatus in FAILED or BLOCKED state
 * @param id - The withdrawal ID
 * @param params - Retry parameters including optional wallet name and from addresses
 * @returns Success response
 */
export async function retryFireblocksWithdrawal(
  id: string,
  params: {
    _yotpToken: string;
    adminRemarks?: string;
    remarkChannel?: string;
    // walletName + walletAccountId are required — the backend rejects the
    // retry otherwise. Callers source both from the resolved /vault-balances
    // response so the retry targets exactly the vault whose balances were
    // reviewed.
    walletName: string;
    walletAccountId: string;
    fromAddresses?: string[];
  }
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/digital-withdraws/${id}/retry-fireblocks`, {
    method: 'POST',
    data: params
  });
}

export type ResetStuckWithdrawalPreflight = {
  decision: 'reset' | 'apply_outcome' | 'blocked';
  id: string;
  status: string;
  processingStatus: string | null;
  rotateIdempotencyKey?: boolean;
  reason?: string;
  transactionHash?: string;
  fireblocksStatus?: string | null;
  fireblocksSubStatus?: string | null;
  fireblocksTransactionId?: string | null;
  proof?: {
    idempotencyKey: string | null;
    fireblocksTransactionId: string | null;
    byExternalId: { outcome: string; status?: string | null; subStatus?: string | null; txHash?: string | null; transactionId?: string | null };
    byTransactionId: { outcome: string; status?: string | null; subStatus?: string | null; txHash?: string | null; transactionId?: string | null };
  };
};

export async function previewResetStuckWithdrawal(id: string): Promise<ResetStuckWithdrawalPreflight> {
  return request<ResetStuckWithdrawalPreflight>(`/digital-withdraws/${id}/reset-preflight`);
}

export async function resetStuckWithdrawalToRequested(
  id: string,
  params: {
    _yotpToken: string;
    adminRemarks: string;
    remarkChannel?: string;
    intent: 'reset' | 'apply_outcome';
  }
): Promise<{ success: boolean; decision: 'reset' | 'apply_outcome'; idempotencyKeyRotated: boolean }> {
  return request(`/digital-withdraws/${id}/reset-to-requested`, {
    method: 'POST',
    data: params
  });
}

export async function bypassReturnWithdrawalAmlRejection(
  id: string,
  params: {
    _yotpToken: string;
    adminRemarks: string;
    remarkChannel?: string;
  }
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/digital-withdraws/${id}/bypass-return-aml`, {
    method: 'POST',
    data: params
  });
}
